import { useState, useCallback, useMemo } from 'react';
import { ISelectOption } from '@creditflowresearch/uilib/Select';

type TStringArrayKeys<T> = {
  [K in keyof T]: T[K] extends string[] ? K : never;
}[keyof T];

interface IFilterContext<T> {
  localFilters: T,
  setLocalFilters: (updater: T | ((prev: T) => T)) => void,
}

interface IUseMultiSelectFilterProps<T> {
  filterKey: TStringArrayKeys<T>,
  options: ISelectOption[],
}

interface IUseMultiSelectFilterReturn {
  selectedValues: ISelectOption[],
  onChange: (selected: ISelectOption[]) => void,
}

const createUseMultiSelectFilter = <T extends Record<string, unknown>>(
  useFilterContext: () => IFilterContext<T>,
) => (
  { filterKey, options }: IUseMultiSelectFilterProps<T>,
): IUseMultiSelectFilterReturn => {
  const { localFilters, setLocalFilters } = useFilterContext();
  const [selectedIds, setSelectedIds] = useState<string[]>(() => localFilters[filterKey] as string[]);

  const selectedValues = useMemo(
    () => options.filter((opt) => selectedIds.includes(String(opt.value))),
    [options, selectedIds],
  );

  const onChange = useCallback((selected: ISelectOption[]): void => {
    const ids = selected.map((opt) => String(opt.value));
    setSelectedIds(ids);
    setLocalFilters((prev) => ({ ...prev, [filterKey]: ids }));
  }, [filterKey, setLocalFilters]);

  return { selectedValues, onChange };
};

export { createUseMultiSelectFilter };
export type { IUseMultiSelectFilterProps, IUseMultiSelectFilterReturn, IFilterContext, TStringArrayKeys };
